import { graphql, useFragment, type FragmentType } from "@repo/graphql-types";
import { format, formatCurrency, ServicePackageEnum } from "@repo/lib";
import { useRouteContext } from "@tanstack/react-router";
import { addDays, parseISO } from "date-fns";
import { HelpCircle } from "@repo/icons";
import { InfoSection } from "@/components/info-section";
import { getAssistVariablePrices } from "@/lib/assist-utils";

export const AssistProAcceptanceCostPredictionCardFragment = graphql(/* GraphQL */ `
  fragment AssistProAcceptanceCostPredictionCardFragment on query_root {
    servicePackage: pacotesServicos_by_pk(codPacoteServicos: $codPacoteServicos) {
        pacotesServicosPreco {
            valorUnitarioFixo
            pacotesServicosPrecosVariaveis {
                valorUnitario
                codPacoteServicosPrecoVariavel
            }
        }
    }

    NuntiusGetCustomerContactManyPeriodSummary(arg1: { from: $from, to: $to }) {
        totalDigitalCustomerContacts
        totalHumanCustomerContacts
    }
  }
`);

interface AssistProAcceptanceCostPredictionCardProps {
  data: FragmentType<typeof AssistProAcceptanceCostPredictionCardFragment>;
}

export const AssistProAcceptanceCostPredictionCard = ({
  data,
}: AssistProAcceptanceCostPredictionCardProps): JSX.Element => {
  const { user } = useRouteContext({ strict: false });
  const fragmentData = useFragment(AssistProAcceptanceCostPredictionCardFragment, data);

  const proPackage = user.pacotesServicos.find(
    (servicePkg) => servicePkg.codPacoteServicos === ServicePackageEnum.Pro.valueOf(),
  );

  const servicePackage = fragmentData.servicePackage?.pacotesServicosPreco;
  const monthlyFixedPrice = servicePackage?.valorUnitarioFixo;

  const { digitalContactVariablePrice, humanContactVariablePrice } =
    getAssistVariablePrices(servicePackage?.pacotesServicosPrecosVariaveis ?? []);

  const customerContactsFromPeriod =
    fragmentData.NuntiusGetCustomerContactManyPeriodSummary;

  const customerContactsTotalCount = customerContactsFromPeriod?.reduce(
    (acc, contact) => {
      return {
        totalDigitalCustomerContacts:
          acc.totalDigitalCustomerContacts + contact.totalDigitalCustomerContacts,
        totalHumanCustomerContacts:
          acc.totalHumanCustomerContacts + contact.totalHumanCustomerContacts,
      };
    },
    {
      totalDigitalCustomerContacts: 0,
      totalHumanCustomerContacts: 0,
    },
  );

  const digitalContactTotalCount =
    customerContactsTotalCount?.totalDigitalCustomerContacts ?? 0;
  const humanContactTotalCount =
    customerContactsTotalCount?.totalHumanCustomerContacts ?? 0;

  const contactsByType = [
    {
      label: "Atendimento Digital",
      price: digitalContactVariablePrice,
      count: digitalContactTotalCount,
    },
    {
      label: "Atendimento Humano",
      price: humanContactVariablePrice,
      count: humanContactTotalCount,
    },
  ].filter((type) => type.price);

  const contactsByTypeTotalSum = contactsByType.reduce(
    (sum, { price, count }) => (price ? sum + price * count : sum),
    0,
  );

  const predictionTotalValue = monthlyFixedPrice
    ? monthlyFixedPrice + contactsByTypeTotalSum
    : "";

  const formattedTrialEndDate = proPackage?.dataFimTrial
    ? format(addDays(parseISO(proPackage.dataFimTrial), 1), "dd 'de' MMM 'de' yyyy")
    : "";

  const fixedPriceSection = {
    title: "",
    fields: [
      {
        label: "Mensalidade",
        value: monthlyFixedPrice ? formatCurrency(monthlyFixedPrice) : "",
        valueSubtext: formattedTrialEndDate
          ? `A partir de ${formattedTrialEndDate}`
          : undefined,
      },
    ],
  };

  const contactDetailsFields = contactsByType.map(({ label, price, count }) => ({
    label,
    labelTag: price ? `${count} X ${formatCurrency(price)}` : "",
    value: price ? formatCurrency(count * price) : "",
  }));

  const contactDetailsSection = {
    title: "",
    fields: contactDetailsFields,
  };

  return (
    <div className="bg-white rounded-2xl p-4 border border-neutral-100 mt-4 flex flex-col gap-6">
      {predictionTotalValue ? (
        <p className="font-medium text-base text-neutral-600">
          Sua previsão de custo baseada no último mês é de{" "}
          {predictionTotalValue ? formatCurrency(predictionTotalValue) : ""} por mês
        </p>
      ) : null}

      <div className="text-sm items-center pb-3 border-b border-neutral-100">
        <InfoSection
          section={fixedPriceSection}
          includeLastLine={false}
          lineColorLevel={100}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between">
          <p className="text-sm text-neutral-500">Cobrança por contato atendido com:</p>
          <HelpCircle className="fill-secondary-300" size={24} />
        </div>

        <div className="bg-neutral-50 rounded-40 w-full p-3">
          <div className="text-sm">
            <InfoSection section={contactDetailsSection} lineColorLevel={100} />
            {servicePackage?.pacotesServicosPrecosVariaveis ? (
              <div className="flex justify-between font-medium mt-3">
                <p>Total</p>
                <span>{formatCurrency(contactsByTypeTotalSum)}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
